import type { PageDesign } from './product-design';

/**
 * What the product page's FIRST render is built from: the server's own read of
 * the product, its seller and its reviews, handed to the client page as props.
 *
 * WHY. product-client.tsx used to render nothing of the real page until the
 * browser had downloaded the app, started Firebase and heard back from a
 * Firestore listener, 5 to 14 seconds on a phone (measured 2026-10-02). Until
 * then visitors saw a text-only stand-in with no gallery, price card or button,
 * which the real page then replaced wholesale. The server already read the same
 * documents for the metadata, so it now hands them over and the page renders
 * complete from the first byte; the live listeners take over in place once they
 * connect (src/firebase/firestore/server-first-snapshot.ts).
 *
 * WHY THIS MODULE IS PURE. The server builds these values and the browser
 * rebuilds the live versions with the same functions, so the two cannot drift
 * into rendering the same document two ways. Both halves, and the test runner,
 * load it.
 */

/**
 * Whether the server may hand this product to the page at all.
 *
 * The server reads with the Admin SDK, which ignores firestore.rules, so this is
 * the line that keeps a seller's draft, or a draft they archived or deleted, out
 * of a page anyone can request. `live` is the one status every branch of the
 * products read rule makes world-readable. Every other status keeps the
 * client-only path, where the browser's own read is what the rules check:
 * owners still reach their archived and deleted purchases that way.
 *
 * Unlisted (`listedOnMarketplace: false`) products ARE served: anyone with the
 * link can already read them, and their `noindex` is what keeps them out of
 * search, not an empty body.
 */
export function isSeedableProduct(data: Readonly<Record<string, unknown>> | null | undefined): boolean {
  return !!data && data.status === 'live';
}

/**
 * How many reviews of each kind (account and guest) the page holds at a time,
 * newest first. "Show More Reviews" widens the account window by another page
 * once the buyer has paged past what is loaded.
 *
 * The listeners used to hold the WHOLE of both subcollections, so every visit
 * read every review the product had ever received and kept paying for each
 * change to any of them. The summary no longer needs them all (it reads the
 * product's own aggregates), and the viewer's own review is read by id, so a
 * window this size loses nothing a visitor can see on arrival.
 *
 * Shared with the server's read, which fetches the same window so the
 * listener's first answer changes nothing on screen.
 */
export const REVIEW_PAGE_SIZE = 50;

/** How many account reviews the page lists before "Show More Reviews". */
export const REVIEWS_SHOWN_ON_ARRIVAL = 5;

/** The seller's public profile, as the product page's seller row and brand read it. */
export type SellerSummary = {
  sellerId: string;
  /** Whether users/{sellerId} exists at all. */
  exists: boolean;
  showAvatar: boolean;
  /** '' when the seller has their picture off, whatever is stored. */
  avatarUrl: string;
  /** '' when the document carries none. */
  username: string;
  tier: string | null;
  /** The seller's storefront brand, consulted only when the product sets no design. */
  design: PageDesign | null;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** A design map's scalar entries: everything it holds, and nothing that cannot cross to the client. */
function plainDesign(value: unknown): PageDesign | null {
  if (!isPlainObject(value)) return null;
  const out: Record<string, string | number | boolean | null> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (entry === null || typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
      out[key] = entry;
    }
  }
  return out as PageDesign;
}

/**
 * The seller row and brand from users/{sellerId}, or from its absence.
 *
 * ONE READING for the server and the browser: page.tsx builds the first render
 * from this, and product-client's own read of the same document goes through it
 * too, so the two cannot disagree about whether the avatar shows.
 *
 * An ABSENT `showProfilePicture` reads as on, as it always has on this page and
 * on product cards (the storefront reads it as off; only accounts created before
 * 2026-03-02 lack the field).
 *
 * @param sellerId The document id read.
 * @param data Its fields, or null when it does not exist.
 */
export function sellerSummaryFromDoc(
  sellerId: string,
  data: Readonly<Record<string, unknown>> | null | undefined,
): SellerSummary {
  if (!data) {
    return { sellerId, exists: false, showAvatar: false, avatarUrl: '', username: '', tier: null, design: null };
  }
  const settings = isPlainObject(data.settings) ? data.settings : undefined;
  const showAvatar = Boolean(settings?.showProfilePicture ?? true);
  const avatarUrl = typeof data.avatarUrl === 'string' ? data.avatarUrl : '';
  return {
    sellerId,
    exists: true,
    showAvatar,
    avatarUrl: showAvatar ? avatarUrl : '',
    username: typeof data.username === 'string' ? data.username : '',
    tier: typeof data.sellerTier === 'string' ? data.sellerTier : null,
    design: plainDesign(data.design),
  };
}

/**
 * An account review as the server hands it over. Dates are epoch ms, which the
 * page turns back into Timestamps, so a seeded review and a live one are the
 * same shape to everything that reads them.
 */
export type ReviewSeed = {
  id: string;
  uid: string;
  /**
   * The author's name from their OWN users document, or '' when it was not
   * looked up. Never the copy stored on the review: that one is written by the
   * reviewer's client and is an unverified claim (see the reviewer fetch in
   * product-client.tsx), and the served HTML must not carry it.
   */
  username: string;
  rating: number;
  text: string;
  createdAtMs: number | null;
  helpfulUids: string[];
  unhelpfulUids: string[];
  reply?: string;
  replyAtMs?: number | null;
  replyHelpfulUids?: string[];
  replyUnhelpfulUids?: string[];
};

/**
 * A guest review as the server hands it over. Deliberately without `orderId`
 * (the guest's payment reference) and `buyerEmail` (older documents carry it):
 * the page needs neither to show a guest review, and both would otherwise sit
 * in HTML that crawlers keep.
 */
export type GuestReviewSeed = {
  id: string;
  rating: number;
  body: string;
  createdAtMs: number | null;
  helpfulUids: string[];
  unhelpfulUids: string[];
  reply?: string;
  replyAtMs?: number | null;
};

/** One reviewer as the page's reviewer fetch resolves them. */
export type ReviewerSeed = {
  exists: boolean;
  /** null: the reviewer has their picture off. '' : on, with no picture. */
  avatarUrl: string | null;
  username?: string;
};

export type ProductReviewSeed = {
  reviews: ReviewSeed[];
  guestReviews: GuestReviewSeed[];
  /** The reviewers of the account reviews shown on arrival, by uid. */
  reviewers: Record<string, ReviewerSeed>;
};

/** Everything page.tsx hands product-client.tsx for a live product. */
export type ProductPageSeed = {
  id: string;
  /** The product document as the server read it (src/lib/cacheable-doc.ts: dates as ISO strings). */
  data: Record<string, unknown>;
  /** Null when the seller read failed: the page then reads the seller itself, as before. */
  seller: SellerSummary | null;
  /** Null when the review read failed: the page then listens for them itself, as before. */
  reviews: ProductReviewSeed | null;
  /** A bundle's products as the server read them (src/lib/product-bundles.ts), paid links stripped. */
  bundleItems?: Array<{ id: string; data: Record<string, unknown> }>;
  /** The seller's live bundles that hold this product, for its "Also in" links, paid links stripped. */
  inBundles?: Array<{ id: string; data: Record<string, unknown> }>;
};

/** A Firestore Timestamp (either SDK), a Date, epoch ms or a date string, as epoch ms. */
export function timestampMs(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const toMillis = (value as { toMillis?: unknown }).toMillis;
  if (typeof toMillis === 'function') {
    const ms = Number(toMillis.call(value));
    return Number.isFinite(ms) ? ms : null;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

/**
 * An account review document as the server hands it over.
 *
 * @param verifiedUsername The author's name from their users document, or ''.
 */
export function reviewSeedFromDoc(
  id: string,
  data: Readonly<Record<string, unknown>>,
  verifiedUsername: string,
): ReviewSeed {
  const seed: ReviewSeed = {
    id,
    uid: typeof data.uid === 'string' ? data.uid : '',
    username: verifiedUsername,
    rating: typeof data.rating === 'number' ? data.rating : 0,
    text: typeof data.text === 'string' ? data.text : '',
    createdAtMs: timestampMs(data.createdAt),
    helpfulUids: stringList(data.helpfulUids),
    unhelpfulUids: stringList(data.unhelpfulUids),
  };
  if (typeof data.reply === 'string') {
    seed.reply = data.reply;
    seed.replyAtMs = timestampMs(data.replyAt);
  }
  if (Array.isArray(data.replyHelpfulUids)) seed.replyHelpfulUids = stringList(data.replyHelpfulUids);
  if (Array.isArray(data.replyUnhelpfulUids)) seed.replyUnhelpfulUids = stringList(data.replyUnhelpfulUids);
  return seed;
}

/** A guest review document as the server hands it over. See GuestReviewSeed for what it leaves out. */
export function guestReviewSeedFromDoc(id: string, data: Readonly<Record<string, unknown>>): GuestReviewSeed {
  const seed: GuestReviewSeed = {
    id,
    rating: typeof data.rating === 'number' ? data.rating : 0,
    body: typeof data.body === 'string' ? data.body : '',
    createdAtMs: timestampMs(data.createdAt),
    helpfulUids: stringList(data.helpfulUids),
    unhelpfulUids: stringList(data.unhelpfulUids),
  };
  if (typeof data.reply === 'string') {
    seed.reply = data.reply;
    seed.replyAtMs = timestampMs(data.replyAt);
  }
  return seed;
}

/**
 * One reviewer, from their users document or its absence: whether to show
 * their picture, and their name. The page's own reviewer fetch reads a
 * snapshot through this too, so the server and the browser resolve a reviewer
 * the same way.
 *
 * An ABSENT `showProfilePicture` reads as OFF here, unlike the seller row
 * above: a reviewer's picture shows only when they have said so.
 */
export function reviewerSeedFromDoc(data: Readonly<Record<string, unknown>> | null | undefined): ReviewerSeed {
  if (!data) return { exists: false, avatarUrl: null };
  const settings = isPlainObject(data.settings) ? data.settings : undefined;
  const showAvatar = Boolean(settings?.showProfilePicture ?? false);
  const reviewer: ReviewerSeed = {
    exists: true,
    avatarUrl: showAvatar ? ((typeof data.avatarUrl === 'string' && data.avatarUrl) || '') : null,
  };
  if (typeof data.username === 'string' && data.username) reviewer.username = data.username;
  return reviewer;
}

/**
 * The "Most Helpful" order the review list opens in: most helpful votes first,
 * ties left in the order given (newest first, as the listener delivers them).
 * The server uses it to pick which reviewers to resolve before the page is
 * sent, so it must be the page's own ordering, not a copy of it.
 */
export function mostHelpfulFirst<R extends { helpfulUids?: readonly string[] }>(reviews: readonly R[]): R[] {
  return [...reviews].sort((a, b) => (b.helpfulUids?.length ?? 0) - (a.helpfulUids?.length ?? 0));
}

/**
 * The uids whose names and pictures the page shows on arrival: the authors of
 * the first REVIEWS_SHOWN_ON_ARRIVAL reviews in the opening order, once each.
 * Before hydration nobody is signed in, so no viewer's own review is pinned to
 * the top, which is exactly the order the server sees too.
 */
export function reviewerUidsShownOnArrival(reviews: readonly ReviewSeed[]): string[] {
  return [
    ...new Set(
      mostHelpfulFirst(reviews)
        .slice(0, REVIEWS_SHOWN_ON_ARRIVAL)
        .map((review) => review.uid)
        .filter(Boolean),
    ),
  ];
}
